/**
 * The capability manifest.
 *
 * The pricing page, the trust page and the security-review packet all
 * render FROM THIS OBJECT rather than from their own prose, so a claim
 * cannot drift between the page, the dashboard and the contract. If P3
 * cannot do something, the only place to say so is here, and every surface
 * inherits it.
 *
 * The rule this enforces: never assert a capability P3 does not have. P3
 * has a documented history of overclaiming (a public homepage showing
 * hiring cost avoided, roles filled and a 3.8x return, none of which is
 * supportable by any field in any P3 system) and this manifest exists
 * partly to make that class of drift impossible to reintroduce quietly.
 */

import { OPENS_COUNTED_FROM } from "~/lib/app-use";

export interface CapabilityManifest {
  readonly shipped: readonly string[];
  readonly plannedFrom: ReadonlyArray<{ item: string; date: string | null }>;
  readonly notOffered: readonly string[];
  readonly suppression: {
    readonly floor: number;
    readonly statement: string;
  };
}

/**
 * What a live dashboard says about the figures P3 records but does not yet
 * count per institution (Sep 11 2026). The snapshot serves these as its
 * sources' reasons and the planned list below says the same thing. They
 * replace a sentence that claimed a new app release was needed: the
 * institution is already on the analytics and on the milestone records, and
 * what is missing is P3's own per-institution reader
 * (Docs/P3 Enterprise/Activity-Scope.md). claims.test.ts holds the wording.
 */
export const APP_ACTIVITY_NOT_COUNTED =
  "Opportunities, Pulse AI and pathway views are recorded in the app, and are not yet counted per institution. Each will publish here at ten students or more.";
/** Pulse AI and pathway views (Sep 11 2026): still not counted per
 *  institution once app activity is. The snapshot serves it as its
 *  `pulseAndPathways` source's reason. */
export const PULSE_AND_PATHWAYS_NOT_COUNTED =
  "Pulse AI sessions and pathway views are recorded in the app, and are not yet counted per institution. Each will publish here at ten students or more.";
/** Pathway views alone (Sep 12 2026): the sentence once Pulse is counted
 *  through Pipe D. Serving the combined sentence then would tell an
 *  institution that a figure on its dashboard is not counted. */
export const PATHWAYS_NOT_COUNTED =
  "Pathway views are recorded in the app, and are not yet counted per institution. They will publish here at ten students or more.";
/** Career Timeline and Pulse AI (Sep 12 2026, Pipe D): what an institution
 *  reads while the web store is not bound. The snapshot serves it as its
 *  `careerTimeline` source's reason. */
export const CAREER_TIMELINE_NOT_COUNTED =
  "Career timelines and Pulse AI sessions are recorded in the web app, and are not yet counted per institution. Each will publish here at ten students or more.";
/**
 * Sub-cohorts (Sep 17 2026): two sources a cohort's document cannot carry.
 * App activity is matched by the institution's NAME in the app's analytics,
 * which know no cohort; the web store's career-timeline aggregate is one
 * call per institution and would be one per cohort. Both stay figures for
 * the whole institution, and a cohort document says so as the source's
 * reason, in the reader's words.
 */
export const APP_ACTIVITY_WHOLE_INSTITUTION =
  "App activity is counted for your whole institution, not for a cohort: the app's analytics name the institution and not the student. Clear the cohort to see it.";
/* The same two sources, said for a slice (Sep 17 2026). Worded for the cut a
   reader actually made, because "Clear the cohort" is not an instruction
   anyone can follow when what they chose was Matched or Waiting. */
export const APP_ACTIVITY_WHOLE_INSTITUTION_SLICE =
  "App activity is counted for your whole institution: the app's analytics name the institution and not the student, so it cannot be split by whether a student has a mentor. Choose All students to see it.";
export const CAREER_TIMELINE_WHOLE_INSTITUTION_SLICE =
  "Career timelines and Pulse AI sessions are counted for your whole institution, and cannot be split by whether a student has a mentor. Choose All students to see them.";
export const CAREER_TIMELINE_WHOLE_INSTITUTION =
  "Career timelines and Pulse AI sessions are counted for your whole institution, not for a cohort. Clear the cohort to see them.";
export const MILESTONES_NOT_COUNTED =
  "Milestones are recorded in the app as your students mark them complete, with the school, major or employer they report. They are not yet counted per institution, and will publish here at ten students or more.";

/**
 * The manifest, true to THIS deployment.
 *
 * `webStore` is whether the Worker is bound to the web store (Pipe D). Pulse AI
 * and Career Timelines are counted per institution only through that binding,
 * so their lines are "working today" only when it exists and stay on the
 * planned list when it does not (Sep 12 2026). A deploy that ships before the
 * binding is switched on therefore claims nothing it cannot count, and the
 * list flips on its own the day `WEB_STORE` is uncommented in wrangler.toml.
 */
export function capabilities(floor: number, webStore = false): CapabilityManifest {
  return {
    shipped: [
      "A join link, a six-character code, and a QR code, generated before your welcome screen finishes painting.",
      "Unlimited free administrator accounts in three roles: owner, admin, viewer.",
      "Your claimed student count, and a weekly growth curve anchored to Sundays.",
      "Students with at least one active mentorship, as a rate carrying its numerator, its denominator, and a 95% interval.",
      "Students who asked for a mentor and have not been matched yet.",
      "What your students say they want help with, their education goal, industry interest and meeting rhythm, each group suppressed below 10.",
      `Questions asked of mentors, mentor videos played and saved, mentors followed, and how many students have none of these on record yet, as running totals from student records, each withheld below ${floor} students.`,
      // Sep 11 2026, after the live check on P3's own tenant (app activity,
      // questions and milestones all read ok): these three moved here from
      // plannedFrom.
      `Students active in the app over the last 90 days, with sessions per student, the average session length, and opportunities applied to through the app, counted per institution each hour from the app's analytics for students whose analytics profile names your institution, each withheld below ${floor} students.`,
      `The share of your students' questions a mentor has answered, and questions asked in the last 90 days, with a weekly running total in the export, each withheld below ${floor} students.`,
      `Milestones your students mark complete in the app, with the outcomes they report in them (a program started, a major declared, an internship or research experience, a first full-time role), counted per institution each hour and withheld below ${floor} students.`,
      // Sep 12 2026. Worded around the DENOMINATOR on purpose: it is the
      // students who can be compared, not the cohort, and a buyer who reads
      // it as a share of their students would be misled about both the
      // figure and how often it appears. It also says BOTH sides, because
      // the complement is held to the floor too (worker.test.ts): a split
      // needs about twenty comparable students, not ten, and promising ten
      // would be a promise the product does not keep.
      `The share of your students whose first full-time role is in the industry on their own profile, counted only over the students for whom P3 holds both, and published once neither side of that share would stand for fewer than ${floor} students. The industry is read from what the student wrote when they marked the milestone complete; the words themselves never leave the system that stores them.`,
      // Pipe D (Sep 12 2026, wording approved by Thomas): from the web store,
      // sessions that hold something the student wrote, deleted ones and
      // Interview Prep excluded (p3-web-store aggregate.ts); and the Career
      // timelines card. Listed only when the binding exists (see above).
      ...(webStore
        ? [
            `Pulse AI sessions and the students who had them, for students 18 and over, counted per institution each hour, each withheld below ${floor} students.`,
            `Career timelines: how many of your students keep one, and their entries by school or role and by past, current or planned, each withheld below ${floor} students.`,
          ]
        : []),
      // Sep 19 2026. Counted from the day P3 began recording an open in the
      // app, so the figure starts where the record starts and says nothing
      // about the months before it.
      `Opportunities opened in the app, with the students behind them, counted per institution each hour from the app's analytics for students whose analytics profile names your institution, each withheld below ${floor} students. P3 began recording an open on ${OPENS_COUNTED_FROM}, and the figure appears once the first one reaches P3 under your institution's name.`,
      "A CSV export with a methods manifest, and a print-ready briefing you can save as PDF and forward.",
      "Your own audit trail: every sign-in, invitation, role change, metrics read and export, exportable by you.",
      // Sep 17 2026 (Thomas): the roster of who has joined.
      "Enrollment: each student who joined your institution, by first name and last initial, with when they joined and whether they have a mentor, and the mentors who name your institution on their profile. Owner and admin accounts only.",
      "The W-9, the IRS determination letter, the Enterprise Subscription Terms, the Data Sharing Addendum and the subprocessor list with data regions, at permanent addresses.",
    ],
    plannedFrom: [
      // A date appears here only when Thomas has committed to it. `null`
      // means "we intend to build this and we will not give you a date",
      // which is a better answer than a date nobody owns.
      // Rewritten Sep 11 2026 with APP_ACTIVITY_NOT_COUNTED and
      // MILESTONES_NOT_COUNTED: recorded today, not yet counted per institution.
      // Narrowed the same evening, when applications and milestones shipped:
      // opens, Pulse AI and pathways are what is still not counted.
      // Narrowed Sep 12 2026 (Thomas approved the wording) when Pulse AI moved
      // to shipped with Pipe D.
      // Sep 19 2026 (Thomas: "start the counter now and going forward"):
      // opportunities opened moved to shipped, so only pathways remain here
      // on a tenant whose web store is bound.
      webStore
        ? { item: "Pathways explored, per institution. P3 records them in the app today and does not yet count them per institution.", date: null }
        : { item: "Pulse AI sessions, career timelines and pathways explored, per institution. P3 records each today and does not yet count it per institution.", date: null },
      // Sep 19 2026: "the verifier is written" came out. No SAML, OIDC or
      // Access code exists in this Worker, so the line says only what is planned.
      { item: "Single sign-on by SAML or OIDC, brokered at the hostname by Cloudflare Access. Not built yet.", date: null },
      // Listed as shipped until Sep 9 2026, when no report existed. It moves
      // back the day a self-assessment is actually written and published.
      { item: "A self-assessed Accessibility Conformance Report (VPAT 2.5 WCAG) for the dashboard.", date: null },
    ],
    notOffered: [
      // Reframed Sep 9 2026 (Thomas, twice): the absence is VERIFICATION, not
      // the data. Students report graduation, a major, a first role and an
      // employer, and the app records applications. See NOT_VERIFIED.
      "A verified outcome record. Graduation, major, first role and employer reach P3 only as a student reports them, in a milestone or their own career timeline, and P3 does not verify them against a registrar or an employer. There is no GPA, credit hours, enrollment status, salary or credential award from an awarding body.",
      "Any dollar figure: return on investment, cost avoided, cost per outcome, hiring cost. P3 holds no cost data for any institution.",
      "Predictive scores, alerts, or student-level flags.",
      // Reframed Sep 17 2026 (Thomas): the roster of who has joined is a list
      // of names, join dates and mentor status. A student's ACTIVITY as one
      // person, and any export of student rows, stay off, and that is what
      // keeps the suppression commitment true.
      "A student's activity as one person, or any export of student records. Enrollment is a list of first names, last initials, join dates and mentor status; what a student does in P3 is reported only across ten or more students, and no export contains a student row.",
      "A named peer institution, or a third-party benchmark. Your cohort is compared against all P3 students in aggregate, and never against a named institution or a national average, because P3 holds no dataset that could support one.",
      "A public API, or any student information system or learning management system integration.",
      "Live data. The dashboard refreshes hourly and the header carries the actual timestamp of the figures you are reading.",
      "A service level agreement, an uptime commitment, or service credits.",
      "SOC 2, or a third-party penetration test report. P3 holds neither and will not represent that it does.",
      "A guarantee that any student is matched with a mentor. We publish the number of approved mentors and we do not promise a match.",
      "Verification that a student who claims your institution attends it. Affiliation is student-asserted.",
      // Closed for good (Thomas, Sep 12 2026): no roster upload, and no list of
      // students by any other route. Worded as final, never "as drafted".
      "Roster upload, or a list of your students from your own systems in any other form. Students add your institution themselves, and P3's Data Sharing Addendum does not claim school-official status under FERPA.",
      "Demographics, race, ethnicity, first-generation status, Pell eligibility, or student geography. None is collected.",
    ],
    suppression: {
      floor,
      statement:
        `Any figure representing fewer than ${floor} students is suppressed before the response leaves P3's servers, and where suppressing one figure would let a reader recover it by subtracting the others from a shown total, the next figure is suppressed as well.`,
    },
  };
}
